import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DAYS_OF_WEEK_FROM_MONDAY } from '@/core/constants/daysOfWeek';
import SelectionItemGroup from '@/components/shared/SelectionItemGroup';
import { LABEL_SX, TRIGGER_TYPES, TriggerType } from './OperationModesSchedulesEditor';
import { required } from '@core/utils/validation.ts';
import FormCheckboxGroup from '@/components/form/FormCheckboxGroup.tsx';
import FormDateSelector from '@/components/form/FormDateSelector.tsx';

interface ScheduleTriggerFieldsProps {
    isMobile: boolean;
    trigger: TriggerType;
    triggerOptions: { label: string; value: TriggerType }[];
    onTriggerChange: (value: TriggerType) => void;
    activeOnDays: string[];
    onDayToggle: (day: string) => void;
    disabledDay: (day: string) => boolean;
    getShortDay: (index: number) => string;
    activeOnDates: string[];
    onDatesChange: (dates: string[]) => void;
    onRemoveDate: (day: string) => void;
    unusedDate: (date: string) => boolean;
}

const ScheduleTriggerFields: React.FC<ScheduleTriggerFieldsProps> = ({
    isMobile,
    trigger,
    triggerOptions,
    onTriggerChange,
    activeOnDays,
    onDayToggle,
    disabledDay,
    getShortDay,
    activeOnDates,
    onDatesChange,
    onRemoveDate,
    unusedDate
}) => {
    const { t } = useTranslation();

    const requiredDates = (val: string[]) => (val?.length ? '' : t('validation.required'));

    const dayCheckboxes = (
        <FormCheckboxGroup
            name="activeOnDays"
            label={t('operationModeScheduleTriggerDay')}
            className={`checkbox-group ${isMobile ? 'checkbox-group-mobile' : ''}`}
            value={activeOnDays}
            rules={[required(t)]}
            items={DAYS_OF_WEEK_FROM_MONDAY.map((dayOfWeek, index) => ({
                key: dayOfWeek,
                checked: activeOnDays.includes(dayOfWeek),
                onChange: () => onDayToggle(dayOfWeek),
                disabled: disabledDay(dayOfWeek),
                label: getShortDay(index)
            }))}
        />
    );

    const dateSelector = (
        <>
            <Box>
                <FormDateSelector
                    name="activeOnDate"
                    multiple
                    value={activeOnDates}
                    onChange={onDatesChange}
                    label={t('operationModeScheduleTriggerDate')}
                    shouldDisableDate={(date) => !unusedDate(date.toISODate() || '')}
                    renderMultipleValue={(dates) => `${dates.length} ${t('days')}`}
                    rules={[requiredDates]}
                    sx={isMobile ? { width: '100%' } : undefined}
                />
            </Box>
            {activeOnDates.length > 0 && (
                <Box className={`date-chip-grid ${isMobile ? 'mobile-grid' : ''}`}>
                    {activeOnDates.map((date) => (
                        <Chip
                            key={date}
                            label={date}
                            onDelete={() => onRemoveDate(date)}
                            size="small"
                            className="email-chip date-chip--grid"
                            deleteIcon={<i className="bi bi-x-circle-fill" />}
                        />
                    ))}
                </Box>
            )}
        </>
    );

    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={LABEL_SX}>{t('operationModeScheduleTrigger')}</Typography>
                    <SelectionItemGroup
                        options={triggerOptions}
                        value={trigger}
                        onChange={(val) => val && onTriggerChange(val as TriggerType)}
                        size="small"
                    />
                </Box>

                {trigger === TRIGGER_TYPES.DAY && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{dayCheckboxes}</Box>
                )}

                {trigger === TRIGGER_TYPES.DATE && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{dateSelector}</Box>
                )}
            </Box>
        );
    }

    const isDate = trigger === TRIGGER_TYPES.DATE;

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mt: 2 }}>
            <Typography sx={{ ...LABEL_SX, pt: '6px' }}>{t('operationModeScheduleTrigger')}</Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 3,
                    rowGap: 2,
                    flexWrap: 'wrap',
                    flex: 1,
                    minWidth: 0
                }}
            >
                <SelectionItemGroup
                    options={triggerOptions}
                    value={trigger}
                    onChange={(val) => val && onTriggerChange(val as TriggerType)}
                    size="small"
                />

                {isDate ? (
                    <Box
                        sx={{
                            flex: '1 1 340px',
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: '500px',
                                maxWidth: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                mt: '-20px'
                            }}
                        >
                            {dateSelector}
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {dayCheckboxes}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ScheduleTriggerFields;
